import type { GatsbyBrowser, PluginOptions } from 'gatsby';
import { ErrorManagement, type ExceptionEvent } from '@printdeal/error-management/react';

import { getDecodedToken, isAuthenticated } from '../../src/helpers/auth';
import { getCurrentVersion, getVersions } from '../../src/helpers/version-check/get-versions';

type Options = PluginOptions & {
  applicationKey: string
  enabled: boolean
  environment: string
  url: string
};

/**
 * If the current user is logged in, send the account ID and customer ID as part of the event
 */
const appendAccountAndCustomerId = async (event: ExceptionEvent) => {
  if (isAuthenticated()) {
    const user = getDecodedToken();
    if (user && user.account && user.customer) {
      return {
        ...event,
        tags: {
          ...event.tags,
          account_id: user.account.id,
          customer_id: user.customer.id,
        },
      };
    }
  }
  return event;
};

/**
 * Add the current and latest build versions to the tags
 */
const appendBuildVersions = async (event: ExceptionEvent) => {
  let extraTags;

  try {
    const { current, latest } = await getVersions();
    extraTags = { versionLoadFailure: false, currentVersion: current, latestVersion: latest };
  } catch {
    extraTags = { versionLoadFailure: true, currentVersion: getCurrentVersion() };
  }

  return {
    ...event,
    tags: {
      ...event.tags,
      ...extraTags,
    },
  };
};

/**
 * If the error is caused by a version mismatch, reload the page instead of sending an error.
 */
const reloadOnVersionMismatch = async (event: ExceptionEvent) => {
  const { currentVersion, latestVersion, versionLoadFailure } = event.tags;

  if (!versionLoadFailure && currentVersion !== latestVersion) {
    window.location.reload();
    return null;
  }

  return event;
};

/**
 * Skip any errors that are caused by page data not being loaded. This is usually indicative of a user navigating away
 * from the site and should not lead to an error.
 */
const ignorePageDataLoadErrors = async (event: ExceptionEvent) => {
  const pageDataRegex = /We couldn't load "\/page-data\/sq\/d\/\d+\.json"/;
  const message = event.exception?.message;
  const { traces } = event;
  if (message && pageDataRegex.test(message) && traces) {
    const pageDataWithoutResponse = traces.some(
      ({ category, data }) => category === 'xhr' && data && message.includes(data.url) && data.status_code === 0,
    );

    if (pageDataWithoutResponse) {
      return null;
    }
  }

  return event;
};

/**
 * Skip any errors about loading the uploader that do not occur on the uploader page. These occur because the user is
 * navigating to another page from the uploader, but the uploader components are refreshed before the new page is
 * loaded, leading to incorrect search parameters being used.
 * @param event
 */
const ignoreUploaderNavigationErrors = async (event: ExceptionEvent) => {
  if (event.fingerprint[0] === 'load-uploader-tool-missing-params' && event.url && !event.url.includes('/uploader')) {
    return null;
  }

  return event;
};

/**
 * Skip any errors that are caused by Microsoft Office scanning our links for potential malware.
 * See https://printdeal.slack.com/archives/C09B3S41V36
 * @param event
 */
const ignoreSafeLinksErrors = async (event: ExceptionEvent) => {
  if (event.exception?.message?.startsWith('Non-Error promise rejection captured with value: Object Not Found Matching Id:')) {
    return null;
  }

  return event;
};

/**
 * Called when the Gatsby browser runtime first starts.
 * @see https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#onClientEntry
 *
 * @public
 */
export const onClientEntry: GatsbyBrowser['onClientEntry'] = async (_, {
  applicationKey, enabled, environment, url,
}: Options) => {
  if (enabled) {
    ErrorManagement.init({
      url,
      // @ts-expect-error
      release: __DYNAMIC_RELEASE_STRING__,
      environment,
      debugIdLocalStorageName: 'debugId',
      ignoreErrors: [
        // See https://printdeal.slack.com/archives/C07SDK6PA05
        'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
        'Hydration Error',
        'https://reactjs.org/docs/error-decoder.html?invariant=418',
        'https://reactjs.org/docs/error-decoder.html?invariant=423',
        'https://reactjs.org/docs/error-decoder.html?invariant=425',
        'Failed to connect to MetaMask',
      ],
      filters: {
        blockedGoogleAds: true,
        axiosErrorWithoutResponse: true,
        thirdPartyErrors: {
          enabled: true,
          applicationKey,
        },
        xhrWithoutResponseInBreadcrumbs: false,
        additionalFilters: [
          appendAccountAndCustomerId,
          appendBuildVersions,
          reloadOnVersionMismatch,
          ignorePageDataLoadErrors,
          ignoreUploaderNavigationErrors,
          ignoreSafeLinksErrors,
        ],
      },
    });
  }
};
